import moment from 'moment';
import { Company, User } from 'stores/interfaces';

export function test() {
	return null;
}

export function getOS() {
	const { userAgent } = window.navigator;
	const { platform } = window.navigator;
	const macosPlatforms = ['Macintosh', 'MacIntel', 'MacPPC', 'Mac68K'];
	const windowsPlatforms = ['Win32', 'Win64', 'Windows', 'WinCE'];
	const iosPlatforms = ['iPhone', 'iPad', 'iPod'];
	let os = null;

	if (macosPlatforms.indexOf(platform) !== -1) {
		os = 'MacOS';
	} else if (iosPlatforms.indexOf(platform) !== -1) {
		os = 'iOS';
	} else if (windowsPlatforms.indexOf(platform) !== -1) {
		os = 'Windows';
	} else if (/Android/.test(userAgent)) {
		os = 'Android';
	} else if (!os && /Linux/.test(platform)) {
		os = 'Linux';
	}

	// @ts-ignore
	document.documentElement.setAttribute('os', os);
	return os;
}

export const hasNotch = () => {
	/**
	 * For storybook test
	 */
	const storybook = window.location !== window.parent.location;
	// @ts-ignore
	const iPhone = /iPhone/.test(navigator.userAgent) && !window.MSStream;
	const aspect = window.screen.width / window.screen.height;
	const aspectFrame = window.innerWidth / window.innerHeight;
	return (
		(iPhone && aspect.toFixed(3) === '0.462') ||
		(storybook && aspectFrame.toFixed(3) === '0.462')
	);
};

export const mergeRefs = (refs: any[]) => {
	return (value: any) => {
		refs.forEach((ref) => {
			if (typeof ref === 'function') {
				ref(value);
			} else if (ref != null) {
				ref.current = value;
			}
		});
	};
};

export const randomColor = () => {
	const colors = ['primary', 'secondary', 'success', 'info', 'warning', 'danger'];

	const color = Math.floor(Math.random() * colors.length);

	return colors[color];
};

export const priceFormat = (amount: number, currency: string) => {
	let locale: string;

	// Map currency codes to locales
	switch (currency.toUpperCase()) {
		case 'USD':
			locale = 'en-US';
			break;
		case 'NGN':
			locale = 'en-NG';
			break;
		case 'EUR':
			locale = 'de-DE'; // You could use another locale appropriate for Euro formatting
			break;
		case 'GBP':
			locale = 'en-GB';
			break;
		// Add more cases as needed
		default:
			locale = 'en-US'; // Fallback locale
	}

	return amount.toLocaleString(locale, {
		style: 'currency',
		currency: currency.toUpperCase(),
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
		useGrouping: true,
	});
};

export const average = (array: any[]) => array.reduce((a, b) => a + b) / array.length;

export const percent = (value1: number, value2: number) =>
	Number(((value1 / value2 - 1) * 100).toFixed(2));

export const getFirstLetter = (text: string, letterCount = 2): string =>
	// @ts-ignore
	text
		.toUpperCase()
		.match(/\b(\w)/g)
		.join('')
		.substring(0, letterCount);

export const debounce = (func: (arg0: any) => void, wait = 1000) => {
	let timeout: string | number | NodeJS.Timeout | undefined;

	return function executedFunction(...args: any[]) {
		const later = () => {
			clearTimeout(timeout);
			// @ts-ignore
			func(...args);
		};

		clearTimeout(timeout);
		timeout = setTimeout(later, wait);
	};
};

export function getEnumValues<T extends string>(enumObj: Record<string, T>): T[] {
	return Object.values(enumObj);
}

export function getEnumKeyByValue<T extends object>(
	enumObj: T,
	value: string,
): keyof T | undefined {
	return (Object.keys(enumObj) as Array<keyof T>).find((key) => enumObj[key] === value);
}

export function getEnumOptions<T extends string>(
	enumObj: Record<string, T>,
): { label: string; value: T }[] {
	return Object.keys(enumObj).map((key) => ({
		label: key,
		value: enumObj[key],
	}));
}

export function checkPermission(checking: string, companyId: number, user?: User | null): boolean {
	let response = false;
	if (companyId && user && user.companies && user.companies) {
		const company = user.companies.find((item: Company) => item.id === companyId);
		if (company) {
			const thePermissions = company.roles_company[0].user_role_permissions;
			for (let i = 0; i < thePermissions.length; i++) {
				if (thePermissions[i].name === checking) {
					response = true;
					break;
				}
			}
		}
	}
	return response;
}

export function getRoleName(companyId: number, user?: User | null): string {
	let response = '';
	if (companyId && user && user.companies && user.companies) {
		const company = user.companies.find((item: Company) => item.id === companyId);
		if (company) response = capitalizeFirstLetter(company.roles_company[0].name);
	}
	return response;
}

export const getCompany = (companyId: number, user: User | null | undefined) => {
	if (!user || !user.companies) return null;
	return user.companies.find((c) => c.id === companyId) || null;
};

export const getProject = (projectId: number, companyId: number, user: User | null | undefined) => {
	const company = getCompany(companyId, user);
	if (!company || !company.projects) return null;
	return company.projects.find((p) => p.id === projectId) || null;
};

export const capitalizeFirstLetter = (string: string) => {
	if (string) {
		return string.charAt(0).toUpperCase() + string.slice(1);
	} else {
		return '';
	}
};

export function ordinalSuffixOf(i: number) {
	const j = i % 10;
	const k = i % 100;
	if (j === 1 && k !== 11) {
		return `${i}st`;
	}
	if (j === 2 && k !== 12) {
		return `${i}nd`;
	}
	if (j === 3 && k !== 13) {
		return `${i}rd`;
	}
	return `${i}th`;
}

export const datFormat = (date: string) => {
	return moment(date).fromNow();
};

export const datFormat2 = (date: string) => {
	return moment(date).format('Do MMMM, YYYY');
};

export const datFormat3 = (date: string) => {
	return moment(date).format('Do MMMM, YYYY hh:mma');
};

export const datFormat4 = (date: string) => {
	return moment(date).format('hh:mm A');
};

export const openPdfForPrint = (base64Data: string) => {
	const base64Prefix = 'data:application/pdf;base64,';
	if (base64Data.startsWith(base64Prefix)) {
		base64Data = base64Data.slice(base64Prefix.length);
	}

	// Decode the Base64 string
	const byteCharacters = atob(base64Data);
	const byteNumbers = new Array(byteCharacters.length);
	for (let i = 0; i < byteCharacters.length; i++) {
		byteNumbers[i] = byteCharacters.charCodeAt(i);
	}
	const byteArray = new Uint8Array(byteNumbers);

	// Create a Blob with the appropriate MIME type
	const blob = new Blob([byteArray], { type: 'application/pdf' });
	const blobUrl = URL.createObjectURL(blob);

	// Open the PDF in a new window
	const newWindow = window.open(blobUrl, '_blank', 'noopener,noreferrer');

	// When the new window has loaded, trigger the print dialog.
	if (newWindow) {
		newWindow.onload = () => {
			newWindow.focus();
			newWindow.print();
		};
	} else {
		console.error('Failed to open new window. Please check your popup blocker settings.');
	}
};

export function getFileExtensionFromBase64(base64String: string): string | null {
	// Regular expression to match the data URL scheme
	const match = base64String.match(/^data:(.*);base64,/);
	if (match && match[1]) {
		// Extract the media type (e.g., image/jpeg, image/png)
		const mediaType = match[1];
		// Split the media type by '/' and return the second part as the file extension
		const extension = mediaType.split('/')[1];
		return extension;
	}
	return null;
}

export const convertFileToBase64 = (file: any) => {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();

		reader.onloadend = () => {
			resolve(reader.result); // Base64 string
		};

		reader.onerror = (error) => {
			reject(error); // Handle error
		};

		reader.readAsDataURL(file); // Convert file to Base64
	});
};

export const formatNumberWithCommasRegex = (num: number) => {
	return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

export function mergeUrlParams(url: string, params: Record<string, string | number>): string {
	// Build the query string from params
	const queryString = Object.entries(params)
		.map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
		.join('&');

	// If url already has a '?', append with '&', otherwise with '?'
	const separator = url.includes('?') ? '&' : '?';
	return `${url}${separator}${queryString}`;
}

export function isValidURL(url: string): boolean {
	try {
		new URL(url);
		return true;
		// eslint-disable-next-line @typescript-eslint/no-unused-vars
	} catch (err) {
		return false;
	}
}
